<script setup>
import { computed } from "vue";
import { useRoute } from "vue-router";

import { TheRouting } from "@/pages";
import { AUTHENTICATION_ROUTE_NAMES, useBreakpoints } from "@/shared";

import AuthenticationDecoration from "./AuthenticationDecoration.vue";

const route = useRoute();
const { isMobile, isTablet } = useBreakpoints();

const isRegistration = computed(
    () => route.name === AUTHENTICATION_ROUTE_NAMES.REGISTRATION
);
const isRestore = computed(() => route.name.match("Restore"));
</script>

<template>
    <div class="authentication">
        <AuthenticationDecoration class="authentication__decoration" />

        <div class="authentication__info-wrapper">
            <a href="https://rentinhand.ru">
                <img
                    class="authentication__logo"
                    src="@/shared/images/RiHLogo.svg"
                    alt="logo"
                />
            </a>
            <p class="authentication__info">
                Современный сервис для автоматизации учёта проката и аренды
            </p>
            <p
                v-if="isRestore && !isMobile && !isTablet"
                class="authentication__important-info"
            >
                Я вспомнил пароль,
                <RouterLink
                    class="authentication__link"
                    :to="{ name: AUTHENTICATION_ROUTE_NAMES.LOGIN }"
                >
                    хочу авторизоваться
                </RouterLink>
            </p>
            <p
                v-if="isRegistration && !isMobile && !isTablet"
                class="authentication__important-info"
            >
                У вас уже есть аккаунт?
                <RouterLink
                    class="authentication__link"
                    :to="{ name: AUTHENTICATION_ROUTE_NAMES.LOGIN }"
                >
                    Авторизоваться
                </RouterLink>
            </p>
        </div>

        <div class="authentication__form-wrapper">
            <TheRouting />
        </div>

        <p
            v-if="isRestore && (isMobile || isTablet)"
            class="authentication__important-info"
        >
            Я вспомнил пароль,
            <RouterLink
                class="authentication__link"
                :to="{ name: AUTHENTICATION_ROUTE_NAMES.LOGIN }"
            >
                хочу авторизоваться
            </RouterLink>
        </p>
        <p
            v-if="isRegistration && (isMobile || isTablet)"
            class="authentication__important-info"
        >
            У вас уже есть аккаунт?
            <RouterLink
                class="authentication__link"
                :to="{ name: AUTHENTICATION_ROUTE_NAMES.LOGIN }"
            >
                Авторизоваться
            </RouterLink>
        </p>
    </div>
</template>

<style lang="scss" scoped>
.authentication {
    position: relative;

    display: flex;

    justify-content: space-between;

    width: 100%;
    height: 100%;

    // padding: 90px 100px 90px 200px;
    padding-left: 200px;

    line-height: var(--default-line-height);

    background: var(--c-background) no-repeat 100% / 100%;

    background-size: cover;

    @media (max-width: 768px) {
        height: unset;

        background: var(--c-neutral-10);
    }

    &__decoration {
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;

        width: 100%;

        @media (max-width: 768px) {
            display: none;
        }
    }

    &__info-wrapper {
        width: calc((450px * 100%) / 1920px);
        padding-top: 150px;

        @media (max-width: 1400px) {
            padding-top: 90px;
        }

        @media (max-width: 1300px) {
            width: 25.5%;
        }

        @media (max-width: 1400px) and (min-height: 1000px) {
            align-self: center;

            padding-top: 0;
        }

        @media (max-width: 1200px) and (min-height: 1023px) {
            transform: translate(0);
        }

        @media (max-width: 992px) {
            width: 100%;
            margin-bottom: 50px;

            text-align: center;

            transform: translate(0);
        }

        @media (max-width: 768px) {
            padding-top: 0;
            margin-bottom: 20px;
        }

        @media (max-width: 350px) {
            margin-bottom: 0;
        }
    }

    &__logo {
        margin-bottom: 40px;

        @media (max-width: 1440px) {
            width: 200px;
        }

        @media (max-width: 992px) {
            margin: 20px auto;
        }

        @media (max-width: 768px) {
            width: 110px;
            margin-right: 10px;
            margin-left: 10px;
        }
    }

    &__heading {
        margin-bottom: 15px;

        font-family: Montserrat, sans-serif;
        font-size: var(--default-h1-size);
        font-weight: 700;
        line-height: var(--default-line-height);

        @media (max-width: 1400px) {
            margin-bottom: 0;
        }

        @media (max-width: 1200px) {
            font-size: 30px;
        }
    }

    &__info {
        margin-bottom: 50px;

        font-size: var(--default-text-size);

        @media (max-width: 1400px) {
            margin-bottom: 20px;
        }

        @media (max-width: 768px) {
            display: none;
        }
    }

    &__important-info {
        margin-bottom: 50px;

        font-size: var(--default-text-size);

        @media (max-width: 1400px) {
            margin-bottom: 20px;
        }
    }

    &__link {
        color: var(--c-text);
        text-decoration: underline;

        &:hover {
            color: var(--c-primary);
            text-decoration: none;
        }
    }

    &__human {
        width: 100%;

        @media (max-width: 992px) {
            display: none;
        }
    }

    &__form-wrapper {
        position: relative;

        display: flex;

        flex-direction: column;

        justify-content: center;

        width: 50vw;
        height: 100%;
        padding: 50px 100px;
        overflow: auto;

        background: var(--c-neutral-10);

        // border-radius: 50px;
        box-shadow: 0 9px 19px rgb(225 225 225 / 11%);

        &::-webkit-scrollbar {
            width: 0;
        }

        @media (max-width: 1400px) {
            padding: 10px 60px;
        }

        @media (max-width: 1200px) and (min-height: 1023px) {
            border-radius: 50px;
        }

        @media (max-width: 992px) {
            width: 100%;

            border-radius: 50px;
        }

        @media (max-width: 768px) {
            height: 100%;
            padding: 10px;

            text-align: center;

            box-shadow: none;
        }
    }

    @media (max-width: 1400px) {
        padding-left: 60px;
    }

    @media (max-width: 1200px) and (min-height: 1023px) {
        flex-direction: column;

        align-items: center;

        padding: 20px 60px;

        border-radius: 50px;

        &__info-wrapper {
            text-align: center;
        }

        &__logo {
            margin: 0 auto;
        }

        &__human {
            width: 50%;
            margin: 0 auto;
        }

        &__info-wrapper,
        &__form-wrapper {
            width: 100%;
        }
    }

    @media (max-width: 992px) {
        flex-direction: column;

        align-items: center;
        justify-content: flex-start;

        padding: 10px 30px;
    }

    @media (max-width: 768px) {
        padding: 10px;

        background-size: 100% 100%;
    }
}
</style>
